import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function ScheduleCall() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", preferred_time: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.preferred_time) {
      toast.error("Please fill in name, phone, email and preferred time.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/schedule-call", form);
      setDone(true);
      toast.success("Request sent! We'll call you back shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-xl border border-black/20 bg-white px-4 py-3 font-500 focus-visible:ring-2 focus-visible:ring-brand-coral";

  return (
    <section id="call" className="relative py-24 md:py-32 bg-brand-blue text-white noise-overlay overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-sm font-700 uppercase tracking-widest text-brand-yellow">Prefer to talk?</span>
          <h2 className="mt-3 font-display font-900 tracking-tight text-4xl md:text-6xl leading-none">
            Let's plan it together.
          </h2>
          <p className="mt-5 text-white/80 font-500 text-lg max-w-md">
            Big event or not sure what you need? Leave your details and a preferred time —
            we'll call you back to build the perfect setup.
          </p>
          <a
            href="https://calendar.app.google/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-ink px-6 py-3 font-700 hover:-translate-y-1 transition-transform"
            data-testid="calendar-link"
          >
            <Phone size={18} /> Or book a time on our calendar
          </a>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-[2rem] bg-white text-ink p-7 md:p-9 border border-black/10"
        >
          {done ? (
            <div className="py-10 text-center" data-testid="call-success">
              <CheckCircle2 size={56} className="mx-auto text-brand-green" />
              <h3 className="mt-4 font-display font-800 text-2xl">You're on the list!</h3>
              <p className="mt-2 text-ink/60 font-500">We'll reach out at your preferred time.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="call-form">
              <div className="grid grid-cols-2 gap-4">
                <Input className={field} placeholder="Full name" value={form.name} onChange={set("name")} data-testid="call-name" />
                <Input className={field} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="call-phone" />
              </div>
              <Input className={field} type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="call-email" />
              <Input className={field} placeholder="Preferred day & time (e.g. Sat afternoon)" value={form.preferred_time} onChange={set("preferred_time")} data-testid="call-time" />
              <Textarea className={field} rows={3} placeholder="Tell us about your event (optional)" value={form.message} onChange={set("message")} data-testid="call-message" />
              <button
                type="submit"
                disabled={loading}
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-brand-coral text-white py-4 font-700 hover:bg-ink transition-colors disabled:opacity-60"
                data-testid="call-submit"
              >
                {loading ? "Sending..." : "Request a Callback"}
                <Send size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
